import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"

import { validatePromoCode } from "./api"
import classNames from "classnames"

import checkmark from "images/components/checkmark.svg"
import "./promo_code_form.scss"

const PromoCodeForm = ({ initialPromoCode, disabled, onValidCodeApplied }) => {
  const [promoCode, setPromoCode] = useState(initialPromoCode)
  const [showInput, setShowInput] = useState(!!initialPromoCode)
  const [couponName, setCouponName] = useState(null)
  const [loading, setLoading] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef()
  const toggleShow = () => setShowInput(!showInput)
  const discountApplied = !loading && couponName
  const promoCodeInvalid = !loading && invalid

  const validate = async () => {
    setLoading(true)
    try {
      const { name } = await validatePromoCode(promoCode)
      setCouponName(name)
      setInvalid(!name)
      if (name) {
        onValidCodeApplied(promoCode)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (showInput) {
      inputRef.current?.focus()
    } else {
      setPromoCode(null)
    }
  }, [showInput, inputRef])

  useEffect(() => {
    if (promoCode) {
      validate()
    }
  }, [])

  return (
    <div
      styleName={classNames("promo-code-field", {
        success: discountApplied,
        error: promoCodeInvalid,
      })}
      className="field"
    >
      <div className="label">
        <label>
          <a className="link-standalone" role="button" onClick={toggleShow}>
            Apply promo
          </a>
        </label>
      </div>
      <div
        className="input"
        styleName={classNames("input-group", {
          show: showInput,
        })}
      >
        <div styleName="input-line">
          <input
            aria-label="promo code"
            ref={inputRef}
            type="text"
            disabled={disabled || loading || couponName}
            value={promoCode || ""}
            onKeyDown={e => e.key === "Enter" && validate()}
            onChange={e => {
              setInvalid(false)
              setCouponName(null)
              setPromoCode(e.target.value)
            }}
          />
          <input
            disabled={disabled || loading || !promoCode || couponName}
            type="submit"
            value="Apply"
            className="button-tertiary"
            onClick={validate}
          />
        </div>
        {discountApplied && (
          <div styleName="infobox">
            <span
              styleName="checkmark"
              dangerouslySetInnerHTML={{ __html: checkmark }}
            />
            {couponName} applied
          </div>
        )}
        {promoCodeInvalid && <div styleName="infobox">Invalid promo code</div>}
      </div>
    </div>
  )
}

PromoCodeForm.propTypes = {
  disabled: PropTypes.bool,
  onValidCodeApplied: PropTypes.func.isRequired,
  initialPromoCode: PropTypes.string,
}

PromoCodeForm.defaultProps = {
  initialPromoCode: "",
}

export default PromoCodeForm
